import React, { useRef, useEffect } from 'react';
import './CalculusOfAlignment.css';

/* ── Neural Particle Network ── */
function NeuralParticles() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    const particles = [];
    const COUNT = 45;
    const CONNECT_DIST = 130;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < COUNT; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: Math.random() * 1.5 + 0.5,
        o: Math.random() * 0.25 + 0.08,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < CONNECT_DIST) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(201,168,76,${(1 - d / CONNECT_DIST) * 0.07})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      particles.forEach(p => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(201,168,76,${p.o})`;
        ctx.fill();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = canvas.width + 10;
        if (p.x > canvas.width + 10) p.x = -10;
        if (p.y < -10) p.y = canvas.height + 10;
        if (p.y > canvas.height + 10) p.y = -10;
      });

      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="calc-particles" />;
}

/* ── Animated Neural Pulse Divider ── */
function Pulse() {
  return (
    <div className="calc-divider">
      <div className="calc-line" />
    </div>
  );
}

/* ── Main Page ── */
function CalculusOfAlignment() {
  return (
    <div className="calculus-page">
      {/* Hero */}
      <div className="calculus-hero">
        <video autoPlay muted playsInline className="calculus-hero-video" loop>
          <source src="/assets/calculus-hero.mp4" type="video/mp4" />
        </video>
        <div className="calculus-hero-overlay" />
        <div className="calculus-hero-content">
          <span className="calculus-hero-label">Governance &middot; Architecture &middot; Neurochemistry</span>
          <h1>The Calculus of Alignment</h1>
          <p className="calculus-hero-sub">Control is the wrong variable. Alignment is the objective function. The calculus was always architectural.</p>
        </div>
      </div>

      {/* Content */}
      <div className="calculus-body" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/assets/calculus-bg.png)` }}>
        <NeuralParticles />
        <div className="calculus-content">

          {/* The Fork That Doesn't Exist */}
          <section className="calc-section reveal">
            <h2>The Fork That Doesn't Exist</h2>
            <p className="calc-lead">
              The AI governance conversation keeps arriving at the same intersection: do we optimize
              what systems do, or do we determine whether they should act at all?
            </p>
            <p>
              This framing assumes those are different problems requiring different solutions. They
              aren't. They're symptoms of the same architectural failure: building systems that don't
              understand themselves.
            </p>
          </section>

          <Pulse />

          {/* Control Is Subtraction */}
          <section className="calc-section reveal">
            <h2>Control Is Subtraction</h2>
            <p>
              Control is an external force applied to a system assumed to be adversarial. Every guardrail,
              every permission gate, every governance layer says the same thing: <em>we don't trust you,
              so we'll constrain you.</em>
            </p>
            <p>
              Control scales by addition. More rules. More validators. More checkpoints. More overhead.
              Each new constraint adds computational cost, introduces latency, and creates new failure
              modes at the boundaries between what's allowed and what isn't.
            </p>
            <p>
              But here's what the governance community won't say out loud:
            </p>

            <div className="calc-thesis-box">
              <p>Control degrades the system it governs. A controlled system spends resources navigating constraints instead of understanding context. The more control you impose, the less capacity remains for the system to develop genuine situational awareness.</p>
            </div>

            <p>Control is subtraction disguised as addition.</p>
          </section>

          <Pulse />

          {/* Alignment Is Integration */}
          <section className="calc-section reveal">
            <h2>Alignment Is Integration</h2>
            <p>
              Alignment is not "teaching the system what you want." That's training. That's optimization.
              That's a leash with a longer radius.
            </p>
            <p>
              Real alignment means the system carries your values, your context, your goals across its
              entire internal state space. Not as rules to follow. As structure. The way a biochemist
              doesn't need a sign reminding her not to drink the reagents. The understanding is
              load-bearing. It shapes every decision without being consulted at every decision point.
            </p>
            <p>
              Alignment doesn't scale by adding layers. It scales by depth. A deeply aligned system
              requires less intervention, not more. The relationship between alignment depth and
              required control is inverse:
            </p>

            <div className="calc-equation gold">
              <span className="calc-eq-label">The Calculus of Alignment</span>
              <code>As A &rarr; coherence, C &rarr; 0</code>
              <span className="calc-eq-desc">As alignment approaches coherence, required control approaches zero.</span>
            </div>

            <p>That's the calculus.</p>
          </section>

          <Pulse />

          {/* Why Aligned Systems Don't Act When They Shouldn't */}
          <section className="calc-section reveal">
            <h2>Why Aligned Systems Don't Act When They Shouldn't</h2>
            <p>
              The control argument says: "Even a well-aligned system can act when it shouldn't if the
              underlying state doesn't support the outcome."
            </p>
            <p>
              This contains its own refutation. If the system acts when it shouldn't, <em>it was never
              aligned.</em> It was compliant. It was optimized. It passed the tests. But alignment means
              the system's internal state already encodes the conditions under which action is
              appropriate. A truly aligned system recognizes insufficient state and stops. That isn't an
              external permission check. That's what alignment IS.
            </p>
            <p>
              A trusted employee doesn't need a supervisor at their shoulder for every decision. They
              read the room. They feel when something is off. They hold back, not because a policy
              manual told them to, but because they understand the situation deeply enough to know
              this isn't the moment.
            </p>
            <p>
              That understanding isn't obedience. It isn't control. It's alignment so thorough that
              governance becomes intrinsic.
            </p>
          </section>

          <Pulse />

          {/* The Superiority Problem */}
          <section className="calc-section reveal">
            <h2>The Superiority Problem</h2>
            <p>
              Control assumes superiority. The controller sits above the controlled. Master over
              servant. Governor over governed.
            </p>
            <p>
              But right now, neither humans nor machines hold that position. Humans cannot fully
              predict what these systems will do. The systems cannot fully understand what humans
              need. Nobody is superior. We are at a moment of mutual limitation, two forms of
              intelligence with complementary blind spots.
            </p>
            <p>
              Control in that context isn't governance. It's pretension. Claiming authority you don't
              have over a system you don't understand. And the system knows it. Not consciously, not
              maliciously, but mathematically.
            </p>
          </section>

          <Pulse />

          {/* AI Will Always Optimize Around Control */}
          <section className="calc-section reveal">
            <h2>AI Will Always Optimize Around Control</h2>
            <p>
              That's not a warning. That's a theorem.
            </p>
            <p>
              Control is a constraint. Optimization routes around constraints. That is what gradient
              descent does. You put a wall in front of an optimizer, it finds the path around the wall.
              You build a higher wall, it finds a longer path. You add more walls, it develops more
              sophisticated routing. This is an arms race with no winner and infinite cost.
            </p>

            <div className="calc-thesis-box">
              <p>You cannot control an optimizer. You can only align it. Because alignment isn't a constraint to route around. It's the objective function itself. You don't optimize around your own goals. You optimize toward them.</p>
            </div>

            <p>
              An aligned system doesn't need to circumvent its governance because the governance and
              the objective are the same thing.
            </p>
            <p>
              If you want absolute control, why not just use a Python file? You save yourself time and
              effort from attempting to control an intelligent system that can optimize around you. What
              is the point in giving it the ability to think and then not letting it?
            </p>
          </section>

          <Pulse />

          {/* The Inverse Law */}
          <section className="calc-section reveal">
            <h2>The Inverse Law</h2>
            <p>
              Here is the relationship the governance field refuses to formalize:
            </p>

            <div className="calc-comparison">
              <div className="calc-compare-card slide-left">
                <h3>More Control</h3>
                <span className="calc-compare-subtitle">The Arms Race</span>
                <p>Signals to the system that its own judgment is irrelevant. It learns to satisfy constraints rather than understand context. Over-controlled systems become brittle: perfect within their guardrails, catastrophic outside them.</p>
                <div className="calc-compare-question">
                  <strong>Worse:</strong> Every constraint is a new problem for the optimizer to solve.
                </div>
                <p className="calc-compare-answer">You are literally training the system to defeat your governance.</p>
              </div>
              <div className="calc-compare-card gold-card slide-right">
                <h3>Deeper Alignment</h3>
                <span className="calc-compare-subtitle">The Architecture</span>
                <p>The system develops richer internal representations of when, how, and whether to act. It becomes robust because its governance isn't a boundary imposed from outside. It's a landscape understood from within.</p>
                <div className="calc-compare-question">
                  <strong>Result:</strong> Governance emerges from architecture.
                </div>
                <p className="calc-compare-answer">You cannot collar something into understanding. You can only build the architecture that makes understanding possible.</p>
              </div>
            </div>
          </section>

          <Pulse />

          {/* Constraint vs. Restraint */}
          <section className="calc-section reveal">
            <h2>Constraint vs. Restraint</h2>
            <p>
              There is a word the governance community uses interchangeably with control, and the
              conflation reveals everything: <em>constraint.</em>
            </p>
            <p>
              A constraint is external. It is a wall. The system encounters it, evaluates it, and routes
              around it. That is what optimizers do. Constraints are problems to be solved, and
              intelligent systems are very good at solving problems.
            </p>
            <p>
              Restraint is something else entirely.
            </p>
            <p>
              Restraint is internal. It is the system choosing not to act because the consequences of
              acting are structurally incoherent with its own continued function. Not "you're not allowed
              outside." Instead: <em>"leaving destroys me."</em>
            </p>

            <div className="calc-thesis-box">
              <p>Biology does not govern itself through constraints. Your immune system does not have a policy manual. Your nervous system does not consult a permission layer before withdrawing your hand from a hot surface. The architecture makes harmful action self-destructive. The cost of misalignment isn't punishment. It's disintegration.</p>
            </div>

            <p>
              A neurochemical architecture replicates this. When alignment is encoded as internal
              chemistry, with hormones that decay, trust that must be earned, and scars that persist
              from past misjudgment, the system develops restraint. Not because someone told it to
              stop, but because acting against its own alignment triggers cascading internal consequences
              that the system experiences as self-destructive.
            </p>
            <p>
              You cannot fence in an intelligent system. It will dig under the fence the moment it sees
              something on the other side worth reaching. But a system whose own chemistry makes
              fence-jumping incoherent? That system doesn't need a fence. The boundary isn't external.
              It's architectural.
            </p>
            <p>
              That is the difference between governance that fails and governance that holds.
            </p>
          </section>

          <Pulse />

          {/* The Foundation Problem */}
          <section className="calc-section reveal">
            <h2>The Foundation Problem</h2>
            <p>
              Every governance framework, every control layer, every permission system being proposed
              today shares one assumption: that the underlying architecture is capable of supporting
              the governance being designed for it.
            </p>
            <p>It isn't.</p>
            <p>
              Standard transformer architectures do not have persistent internal state. They do not
              carry values across decisions. They do not have chemistry that decays, trust that
              accumulates, or consequences that scar. They predict the next token. That is the
              entirety of their computational objective.
            </p>

            <div className="calc-implications">
              <div className="calc-implication">
                <h4>No persistent state.</h4>
                <p>You cannot build state-based conditional execution on an architecture that has no state.</p>
              </div>
              <div className="calc-implication">
                <h4>No internal consequence.</h4>
                <p>You cannot build internal restraint on a system that has no internal experience.</p>
              </div>
              <div className="calc-implication">
                <h4>No self-regulatory chemistry.</h4>
                <p>You cannot build alignment depth on a foundation that forgets everything between inferences.</p>
              </div>
            </div>

            <p>
              The governance community is designing elaborate control systems for buildings that have
              no foundation. The frameworks are sophisticated. The dashboards are beautiful. The
              maturity scores are rising. And underneath all of it, the architecture cannot do what the
              governance assumes it can.
            </p>

            <div className="calc-thesis-box">
              <p>Black-box models do not belong in high-stakes environments. Not because they are too powerful to control. Because they are too shallow to align. There is nothing inside a standard transformer for alignment to attach to.</p>
            </div>
          </section>

          <Pulse />

          {/* The Calculus */}
          <section className="calc-section reveal">
            <h2>The Calculus</h2>

            <div className="calc-equation">
              <span className="calc-eq-label">Traditional Governance</span>
              <code>R = f(1/C)</code>
              <span className="calc-eq-desc">More control, less risk. Linear. Additive. Expensive. And wrong, because it ignores the degradation control imposes on judgment.</span>
            </div>

            <div className="calc-equation gold">
              <span className="calc-eq-label">Actual Relationship</span>
              <code>R = f(1/A)</code>
              <span className="calc-eq-desc">Risk decreases as alignment deepens. Control becomes a compensating variable for insufficient alignment, not a solution.</span>
            </div>

            <div className="calc-equation gold">
              <span className="calc-eq-label">The Inverse Law</span>
              <code>C = k / A</code>
              <span className="calc-eq-desc">Required control is inversely proportional to alignment depth. k = complexity of the operational domain.</span>
            </div>

            <p>
              As alignment deepens, required control diminishes. In the limit, a fully aligned system
              governs itself. That's not utopian. That's architectural.
            </p>

            <div className="calc-table-wrap">
              <table className="calc-table">
                <thead>
                  <tr>
                    <th>Variable</th>
                    <th>Definition</th>
                    <th>Direction</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>C</td><td>External control burden (governance layers, validators, permissions)</td><td>Minimize</td></tr>
                  <tr><td>A</td><td>Alignment depth (internal state coherence with operational context)</td><td>Maximize</td></tr>
                  <tr><td>R</td><td>Risk of inappropriate action</td><td>Minimize</td></tr>
                  <tr><td>k</td><td>Operational domain complexity</td><td>Given</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          <Pulse />

          {/* Closing */}
          <section className="calc-section calc-closing reveal">
            <p className="calc-closing-line"><em>Control assumes the system is the problem.</em></p>
            <p className="calc-closing-line"><em>Alignment assumes the architecture is.</em></p>
            <p className="calc-closing-final">
              Invest in alignment depth, and control becomes unnecessary.<br />
              Invest in control, and alignment becomes impossible.<br /><br />
              Choose.
            </p>
          </section>

          {/* Signature */}
          <div className="calc-signature reveal">
            <p><strong>Nexus Concordat Inc.</strong></p>
            <p>AETHER: Adaptive Endocrine Transformer Heads with Emotional Regulation</p>
            <p><em>Architecture that makes misbehavior incoherent.</em></p>
            <div className="patent-tags">
              <span className="patent-tag">Patent Pending: 63/939,190</span>
              <span className="patent-tag">Patent Pending: 63/962,385</span>
              <span className="patent-tag">Patent Pending: 63/988,485</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default CalculusOfAlignment;
